'use client';

import React, { useState } from 'react';
import {
  CalendarDays,
  Clock,
  ChevronLeft,
  ChevronRight,
  CheckCircle2,
  AlertCircle,
  Stethoscope,
  User,
  FileText,
  X,
} from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


type AppointmentType = 'New Consultation' | 'Follow-up' | 'Spirometry' | 'Emergency';
type TimeSlot = { time: string; available: boolean };

interface BookedAppointment {
  date: string;
  time: string;
  type: AppointmentType;
  status: 'Confirmed' | 'Pending' | 'Cancelled';
  notes: string;
}

const appointmentTypes: AppointmentType[] = ['New Consultation', 'Follow-up', 'Spirometry', 'Emergency'];

const timeSlots: TimeSlot[] = [
  { time: '08:30 AM', available: false },
  { time: '09:00 AM', available: false },
  { time: '09:30 AM', available: true },
  { time: '10:00 AM', available: false },
  { time: '10:30 AM', available: true },
  { time: '11:00 AM', available: true },
  { time: '11:30 AM', available: false },
  { time: '02:00 PM', available: true },
  { time: '02:30 PM', available: true },
  { time: '03:00 PM', available: false },
  { time: '03:30 PM', available: true },
  { time: '04:00 PM', available: true },
];

const myAppointments: BookedAppointment[] = [
  { date: '14 Aug 2026', time: '10:30 AM', type: 'Follow-up', status: 'Confirmed', notes: 'COPD routine review' },
  { date: '28 Aug 2026', time: '11:00 AM', type: 'Spirometry', status: 'Pending', notes: 'Lung function test' },
  { date: '01 Jul 2026', time: '09:30 AM', type: 'Follow-up', status: 'Confirmed', notes: 'Post-discharge check' },
];

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

function getDaysInMonth(year: number, month: number) {
  return new Date(year, month + 1, 0).getDate();
}
function getFirstDayOfMonth(year: number, month: number) {
  return new Date(year, month, 1).getDay();
}

export default function BookAppointmentPage() {
  const today = new Date(2026, 7, 12); // Aug 12 2026
  const [viewMonth, setViewMonth] = useState(today.getMonth());
  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [selectedDate, setSelectedDate] = useState<number | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<string | null>(null);
  const [selectedType, setSelectedType] = useState<AppointmentType>('Follow-up');
  const [notes, setNotes] = useState('');
  const [step, setStep] = useState<'pick' | 'confirm' | 'success'>('pick');
  const [activeTab, setActiveTab] = useState<'book' | 'my'>('book');

  const daysInMonth = getDaysInMonth(viewYear, viewMonth);
  const firstDay = getFirstDayOfMonth(viewYear, viewMonth);

  const prevMonth = () => {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y - 1); }
    else setViewMonth(m => m - 1);
    setSelectedDate(null); setSelectedSlot(null);
  };
  const nextMonth = () => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y + 1); }
    else setViewMonth(m => m + 1);
    setSelectedDate(null); setSelectedSlot(null);
  };

  const isPast = (day: number) => {
    const d = new Date(viewYear, viewMonth, day);
    return d < new Date(today.getFullYear(), today.getMonth(), today.getDate());
  };

  const statusColor = (s: BookedAppointment['status']) => {
    if (s === 'Confirmed') return 'text-positive bg-positive-bg';
    if (s === 'Pending') return 'text-warning bg-warning-bg';
    return 'text-danger bg-danger-bg';
  };

  if (step === 'success') {
    return (
      <div className="max-w-lg mx-auto px-4 pt-16 flex flex-col items-center text-center space-y-4">
        <div className="w-20 h-20 bg-positive-bg rounded-full flex items-center justify-center">
          <CheckCircle2 size={40} className="text-positive" />
        </div>
        <h2 className="text-xl font-800 text-foreground">Appointment Requested!</h2>
        <p className="text-sm text-muted-foreground leading-relaxed">
          Your appointment request for <strong>{MONTHS[viewMonth]} {selectedDate}, {viewYear}</strong> at <strong>{selectedSlot}</strong> has been sent to Dr. Neeraj Mumwalia. You will receive a confirmation shortly.
        </p>
        <button
          onClick={() => { setStep('pick'); setSelectedDate(null); setSelectedSlot(null); setNotes(''); }}
          className="mt-4 bg-primary text-white font-600 text-sm px-6 py-3 rounded-xl hover:bg-primary/90 transition-colors"
        >
          Book Another
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-lg mx-auto px-4 pt-6 space-y-5">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-800 text-foreground">Book Appointment</h1>
        <p className="text-sm text-muted-foreground mt-1">Schedule a visit with Dr. Neeraj Mumwalia</p>
      </div>

      {/* Doctor Card */}
      <div className="bg-card border border-border rounded-2xl p-4 flex items-center gap-4">
        <div className="w-14 h-14 bg-secondary rounded-xl flex items-center justify-center shrink-0">
          <Stethoscope size={24} className="text-primary" />
        </div>
        <div>
          <p className="font-700 text-foreground">Dr. Neeraj Mumwalia</p>
          <p className="text-xs text-muted-foreground mt-0.5">Pulmonologist & Chest Physician</p>
          <p className="text-xs text-muted-foreground">Centre for Lung Care and Chest Diseases, Jammu</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex bg-muted rounded-xl p-1 gap-1">
        {(['book', 'my'] as const).map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`flex-1 py-2 text-sm font-600 rounded-lg transition-all ${
              activeTab === tab ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground'
            }`}
          >
            {tab === 'book' ? 'Book New' : 'My Appointments'}
          </button>
        ))}
      </div>

      {activeTab === 'my' ? (
        <div className="space-y-3">
          {myAppointments.map((appt, i) => (
            <div key={i} className="bg-card border border-border rounded-xl p-4">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="text-sm font-700 text-foreground">{appt.date} · {appt.time}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{appt.type}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{appt.notes}</p>
                </div>
                <span className={`text-[10px] font-700 px-2.5 py-1 rounded-full shrink-0 ${statusColor(appt.status)}`}>
                  {appt.status}
                </span>
              </div>
              {appt.status === 'Confirmed' && (
                <button className="mt-3 w-full flex items-center justify-center gap-1.5 border border-danger/30 text-danger text-xs font-600 py-2 rounded-lg hover:bg-danger-bg transition-colors">
                  <X size={13} /> Cancel Appointment
                </button>
              )}
            </div>
          ))}
        </div>
      ) : step === 'pick' ? (
        <>
          {/* Appointment Type */}
          <div>
            <p className="text-xs font-700 text-muted-foreground uppercase tracking-wide mb-2">Appointment Type</p>
            <div className="grid grid-cols-2 gap-2">
              {appointmentTypes.map((type) => (
                <button
                  key={type}
                  onClick={() => setSelectedType(type)}
                  className={`py-2.5 px-3 rounded-xl text-sm font-600 border transition-all text-left ${
                    selectedType === type
                      ? 'bg-primary text-white border-primary' :'bg-card text-foreground border-border hover:border-primary/40'
                  }`}
                >
                  {type}
                </button>
              ))}
            </div>
          </div>

          {/* Calendar */}
          <div className="bg-card border border-border rounded-2xl p-4">
            <div className="flex items-center justify-between mb-4">
              <button onClick={prevMonth} className="p-1.5 rounded-lg hover:bg-muted transition-colors">
                <ChevronLeft size={18} className="text-foreground" />
              </button>
              <p className="text-sm font-700 text-foreground">{MONTHS[viewMonth]} {viewYear}</p>
              <button onClick={nextMonth} className="p-1.5 rounded-lg hover:bg-muted transition-colors">
                <ChevronRight size={18} className="text-foreground" />
              </button>
            </div>
            <div className="grid grid-cols-7 mb-2">
              {DAYS.map((d) => (
                <div key={d} className="text-center text-[10px] font-700 text-muted-foreground py-1">{d}</div>
              ))}
            </div>
            <div className="grid grid-cols-7 gap-y-1">
              {Array.from({ length: firstDay }).map((_, i) => <div key={`empty-${i}`} />)}
              {Array.from({ length: daysInMonth }).map((_, i) => {
                const day = i + 1;
                const past = isPast(day);
                const selected = selectedDate === day;
                return (
                  <button
                    key={day}
                    disabled={past}
                    onClick={() => { setSelectedDate(day); setSelectedSlot(null); }}
                    className={`mx-auto w-9 h-9 rounded-full text-sm font-600 transition-all flex items-center justify-center ${
                      selected ? 'bg-primary text-white' : past ?'text-muted-foreground/40 cursor-not-allowed': 'text-foreground hover:bg-secondary'
                    }`}
                  >
                    {day}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Time Slots */}
          {selectedDate && (
            <div>
              <p className="text-xs font-700 text-muted-foreground uppercase tracking-wide mb-2">
                Available Slots — {MONTHS[viewMonth]} {selectedDate}
              </p>
              <div className="grid grid-cols-3 gap-2">
                {timeSlots.map((slot) => (
                  <button
                    key={slot.time}
                    disabled={!slot.available}
                    onClick={() => setSelectedSlot(slot.time)}
                    className={`py-2.5 rounded-xl text-xs font-600 border transition-all ${
                      !slot.available ? 'bg-muted text-muted-foreground/50 border-border cursor-not-allowed line-through' :
                      selectedSlot === slot.time ? 'bg-primary text-white border-primary': 'bg-card text-foreground border-border hover:border-primary/40'
                    }`}
                  >
                    {slot.time}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Notes */}
          {selectedSlot && (
            <div>
              <p className="text-xs font-700 text-muted-foreground uppercase tracking-wide mb-2">Reason / Notes (optional)</p>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Describe your symptoms or reason for visit…"
                rows={3}
                className="w-full bg-input border border-border rounded-xl px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-none"
              />
            </div>
          )}

          {selectedSlot && (
            <button
              onClick={() => setStep('confirm')}
              className="w-full bg-primary text-white font-700 text-sm py-3.5 rounded-xl hover:bg-primary/90 transition-colors"
            >
              Review Appointment
            </button>
          )}
        </>
      ) : (
        /* Confirm Step */
        <div className="space-y-4">
          <div className="bg-secondary border border-primary/20 rounded-2xl p-5 space-y-3">
            <p className="text-xs font-700 text-primary uppercase tracking-wide">Appointment Summary</p>
            {[
              { icon: User, label: 'Doctor', value: 'Dr. Neeraj Mumwalia' },
              { icon: CalendarDays, label: 'Date', value: `${MONTHS[viewMonth]} ${selectedDate}, ${viewYear}` },
              { icon: Clock, label: 'Time', value: selectedSlot! },
              { icon: Stethoscope, label: 'Type', value: selectedType },
              ...(notes ? [{ icon: FileText, label: 'Notes', value: notes }] : []),
            ].map(({ icon: Icon, label, value }) => (
              <div key={label} className="flex items-start gap-3">
                <Icon size={15} className="text-primary mt-0.5 shrink-0" />
                <div>
                  <p className="text-[10px] text-muted-foreground font-600 uppercase">{label}</p>
                  <p className="text-sm font-600 text-foreground">{value}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="flex items-center gap-2 bg-warning-bg border border-warning/20 rounded-xl px-4 py-3">
            <AlertCircle size={15} className="text-warning shrink-0" />
            <p className="text-xs text-warning font-500">Appointment is subject to doctor availability. You will be notified once confirmed.</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => setStep('pick')}
              className="flex-1 border border-border text-foreground font-600 text-sm py-3 rounded-xl hover:bg-muted transition-colors"
            >
              Edit
            </button>
            <button
              onClick={() => setStep('success')}
              className="flex-1 bg-primary text-white font-700 text-sm py-3 rounded-xl hover:bg-primary/90 transition-colors"
            >
              Confirm Booking
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
